import React, { useState } from 'react';
import { Modal, ScrollView, Pressable, KeyboardAvoidingView, Platform } from 'react-native';
import { YStack, XStack, H4, SizableText, Button, Input, TextArea } from 'tamagui';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';

interface CreateTopicModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: {
    title: string;
    description: string;
    icon?: string;
    color?: string;
    tags?: string[];
  }) => void;
  loading?: boolean;
}

const TOPIC_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', 
  '#8b5cf6', '#06b6d4', '#84cc16', '#f97316',
  '#ec4899', '#6b7280'
];

const TOPIC_ICONS = [
  'forum', 'lightbulb', 'help-circle', 'book-open',
  'chat', 'information', 'school', 'tools',
  'heart', 'star', 'flag', 'trophy'
];

const CreateTopicModal: React.FC<CreateTopicModalProps> = ({
  visible,
  onClose,
  onSubmit,
  loading = false
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedColor, setSelectedColor] = useState(TOPIC_COLORS[0]);
  const [selectedIcon, setSelectedIcon] = useState(TOPIC_ICONS[0]);
  const [tags, setTags] = useState('');
  const theme = themeSelector();

  const colors = {
    background: theme ? '#ffffff' : '#1f2937',
    modalBackground: theme ? '#f8fafc' : '#374151',
    cardBackground: theme ? '#ffffff' : '#4b5563',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    border: theme ? '#e5e7eb' : '#4b5563',
    overlay: 'rgba(0, 0, 0, 0.5)',
  };

  const handleSubmit = () => {
    if (!title.trim()) return;

    const tagArray = tags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    onSubmit({
      title: title.trim(),
      description: description.trim(),
      icon: selectedIcon,
      color: selectedColor,
      tags: tagArray.length > 0 ? tagArray : undefined,
    });

    // Reset form
    setTitle('');
    setDescription('');
    setSelectedColor(TOPIC_COLORS[0]);
    setSelectedIcon(TOPIC_ICONS[0]);
    setTags('');
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      // Reset form on close
      setTitle('');
      setDescription('');
      setSelectedColor(TOPIC_COLORS[0]);
      setSelectedIcon(TOPIC_ICONS[0]);
      setTags('');
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <Pressable
          style={{
            flex: 1,
            backgroundColor: colors.overlay,
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20,
          }}
          onPress={handleClose}
        >
          <Pressable
            onPress={(e) => e.stopPropagation()}
            style={{
              backgroundColor: colors.background,
              borderRadius: 16,
              width: '100%',
              maxWidth: 500,
              maxHeight: '90%',
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.3,
              shadowRadius: 8,
              elevation: 8,
            }}
          >
            <Animatable.View animation="slideInUp" duration={300}>
              {/* Header */}
              <XStack
                padding="$4"
                alignItems="center"
                justifyContent="space-between"
                borderBottomWidth={1}
                borderBottomColor={colors.border}
              >
                <H4 color={colors.text}>Create New Topic</H4>
                <Pressable onPress={handleClose} disabled={loading}>
                  <MaterialIcons
                    name="close"
                    size={24}
                    color={colors.textSecondary}
                  />
                </Pressable>
              </XStack>

              <ScrollView
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{ padding: 16 }}
              >
                <YStack gap="$4">
                  {/* Title */}
                  <YStack gap="$2">
                    <SizableText color={colors.text} fontWeight="600">
                      Topic Title *
                    </SizableText>
                    <Input
                      placeholder="Enter topic title..."
                      value={title}
                      onChangeText={setTitle}
                      backgroundColor={colors.modalBackground}
                      borderColor={colors.border}
                      color={colors.text}
                      maxLength={100}
                    />
                    <SizableText color={colors.textSecondary} size="$2">
                      {title.length}/100 characters
                    </SizableText>
                  </YStack>

                  {/* Description */}
                  <YStack gap="$2">
                    <SizableText color={colors.text} fontWeight="600">
                      Description
                    </SizableText>
                    <TextArea
                      placeholder="Describe what this topic is about..."
                      value={description}
                      onChangeText={setDescription}
                      backgroundColor={colors.modalBackground}
                      borderColor={colors.border}
                      color={colors.text}
                      minHeight={80}
                      maxLength={500}
                      textAlignVertical="top"
                    />
                    <SizableText color={colors.textSecondary} size="$2">
                      {description.length}/500 characters
                    </SizableText>
                  </YStack>

                  {/* Icon Selection */}
                  <YStack gap="$2">
                    <SizableText color={colors.text} fontWeight="600">
                      Icon
                    </SizableText>
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      contentContainerStyle={{ paddingHorizontal: 4 }}
                    >
                      <XStack gap="$2" paddingVertical="$2">
                        {TOPIC_ICONS.map((icon) => (
                          <Pressable
                            key={icon}
                            onPress={() => setSelectedIcon(icon)}
                            style={{
                              width: 50,
                              height: 50,
                              borderRadius: 25,
                              backgroundColor: selectedIcon === icon 
                                ? `${selectedColor}20` 
                                : colors.modalBackground,
                              borderWidth: 2,
                              borderColor: selectedIcon === icon 
                                ? selectedColor 
                                : colors.border,
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <MaterialCommunityIcons
                              name={icon as any}
                              size={24}
                              color={selectedIcon === icon ? selectedColor : colors.textSecondary}
                            />
                          </Pressable>
                        ))}
                      </XStack>
                    </ScrollView>
                  </YStack>

                  {/* Color Selection */}
                  <YStack gap="$2">
                    <SizableText color={colors.text} fontWeight="600">
                      Color Theme
                    </SizableText>
                    <XStack gap="$2" flexWrap="wrap" paddingVertical="$2">
                      {TOPIC_COLORS.map((color) => (
                        <Pressable
                          key={color}
                          onPress={() => setSelectedColor(color)}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 20,
                            backgroundColor: color,
                            borderWidth: 3,
                            borderColor: selectedColor === color ? colors.text : 'transparent',
                            margin: 2,
                          }}
                        />
                      ))}
                    </XStack>
                  </YStack>

                  {/* Tags */}
                  <YStack gap="$2">
                    <SizableText color={colors.text} fontWeight="600">
                      Tags (Optional)
                    </SizableText>
                    <Input
                      placeholder="Enter tags separated by commas..."
                      value={tags}
                      onChangeText={setTags}
                      backgroundColor={colors.modalBackground}
                      borderColor={colors.border}
                      color={colors.text}
                    />
                    <SizableText color={colors.textSecondary} size="$2">
                      Separate multiple tags with commas (e.g., help, discussion, guide)
                    </SizableText>
                  </YStack>

                  {/* Preview */}
                  {title && (
                    <YStack gap="$2">
                      <SizableText color={colors.text} fontWeight="600">
                        Preview
                      </SizableText>
                      <XStack
                        gap="$3"
                        padding="$3"
                        backgroundColor={colors.modalBackground}
                        borderRadius="$3"
                        borderWidth={1}
                        borderColor={colors.border}
                        alignItems="center"
                      >
                        <YStack
                          width={48}
                          height={48}
                          backgroundColor={`${selectedColor}15`}
                          borderRadius={24}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <MaterialCommunityIcons
                            name={selectedIcon as any}
                            size={24}
                            color={selectedColor}
                          />
                        </YStack>
                        <YStack flex={1} gap="$1">
                          <SizableText color={colors.text} fontWeight="600">
                            {title}
                          </SizableText>
                          {description && (
                            <SizableText 
                              color={colors.textSecondary} 
                              size="$2"
                              numberOfLines={2}
                            >
                              {description}
                            </SizableText>
                          )}
                        </YStack>
                      </XStack>
                    </YStack>
                  )}
                </YStack>
              </ScrollView>

              {/* Footer */}
              <XStack
                padding="$4"
                justifyContent="space-between"
                borderTopWidth={1}
                borderTopColor={colors.border}
                gap="$2"
              >
                <Button
                  flex={1}
                  variant="outlined"
                  borderColor={colors.border}
                  color={colors.textSecondary}
                  onPress={handleClose}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  flex={1}
                  backgroundColor={selectedColor}
                  color="white"
                  onPress={handleSubmit}
                  disabled={!title.trim() || loading}
                  opacity={(!title.trim() || loading) ? 0.5 : 1}
                  icon={loading ? (
                    <MaterialCommunityIcons name="loading" size={18} color="white" />
                  ) : (
                    <MaterialCommunityIcons name="plus" size={18} color="white" />
                  )}
                >
                  {loading ? 'Creating...' : 'Create Topic'}
                </Button>
              </XStack>
            </Animatable.View>
          </Pressable>
        </Pressable>
      </KeyboardAvoidingView>
    </Modal>
  );
};

export default CreateTopicModal;
